import type { SkinModality, SkinRuntimeManifest, SkinVisualPlan } from '@kenocyber/shared';
import { findSkinEntry, planForSkin, skinTextureKey } from './skin-runtime';

/**
 * Turning what the server said into what this build may draw (gate V23.1C).
 *
 * ## The one thing the wire may decide, and the one thing it may not
 *
 * The wire says a `definitionId`. That is a CONTENT identity: it is looked up
 * in this build's own validated manifest, and anything not found there draws
 * nothing. The wire never says an asset key, a file name or a descriptor,
 * because a client that accepted one would be drawing whatever the sender
 * named - and in a game with ownership, what the sender says is influenced by
 * data a player can affect.
 *
 * `instanceId` is deliberately unused here, and the signature says so by not
 * taking one. Two copies of a skin are two economic objects and one picture;
 * letting the copy choose the art would be inventing a difference the content
 * does not have.
 *
 * ## Draft content is gated by the build, not by this function
 *
 * `findSkinEntry` filters through `listSelectableSkins`, which returns `ready`
 * entries always and everything else only when `DEV_TOOLS_ALLOWED` is compiled
 * in. Vite replaces that constant, so the branch is *removed* from a production
 * bundle rather than being a check somebody could flip. This function does not
 * re-implement the rule - it inherits it, which is why a draft skin equipped in
 * production resolves to null here without a line of its own.
 *
 * ## Null is a normal answer
 *
 * No skin, an unknown id, a draft entry in a real build, a manifest that never
 * loaded: every one of them is `null`, and `null` means the normal hull. There
 * is no error state a caller has to distinguish, because the honest outcome of
 * all of them is the same picture.
 */

/** What a scene needs to dress a ship, or nothing at all. */
export interface ResolvedSkinContent {
  readonly plan: SkinVisualPlan;
  /** The Phaser texture key the caller must have loaded before applying. */
  readonly textureKey: string;
  /** Echoed back so a caller can log or assert WHICH content was chosen. */
  readonly definitionId: string;
}

/** What the bootstrap's `loadout.skin` carries. Nothing else is accepted. */
export interface WornSkinIdentity {
  readonly instanceId: string;
  readonly definitionId: string;
}

/**
 * Resolves the worn skin's content for one mode.
 *
 * Pure and deterministic: same wire plus same manifest plus same modality, same
 * answer. No fetch, no clock, no randomness and nothing per frame - a caller may
 * hold the result for as long as the loadout does not change.
 *
 * ## Why the modality is a parameter and never a default
 *
 * Because it is the whole of the level-of-detail decision, and it belongs to
 * the CALLER's mode rather than to the skin. The descriptor authors three
 * profiles - `hunt`, `pvp1x1` and `pvp3x3` - and the last one already halves
 * the cadence, the lifespan and the particle ceiling and drops the loudest
 * signature, because six ships cost more than two. A default would let a duel
 * quietly draw the hunt's profile with six hulls on screen, which is the exact
 * mistake the three profiles exist to make impossible. So every call site says
 * which mode it is, and there is no value to fall back to.
 */
export function resolveSkinContent(
  worn: WornSkinIdentity | null,
  manifest: SkinRuntimeManifest | null,
  modality: SkinModality,
): ResolvedSkinContent | null {
  if (!worn) {
    return null;
  }
  /*
   * Looked up by `definitionId` ALONE.
   *
   * `worn.instanceId` is never read, and that is the point rather than an
   * omission: which copy is worn is an economic fact the wardrobe cares about,
   * and the art is a property of the definition.
   */
  const entry = findSkinEntry(manifest, worn.definitionId);
  if (!entry) {
    return null;
  }
  return {
    plan: planForSkin(entry, modality, 'full'),
    textureKey: skinTextureKey(entry.definitionId),
    definitionId: entry.definitionId,
  };
}

/**
 * Whether two resolutions need the renderer torn down and rebuilt.
 *
 * The texture key is the whole test. Swapping between two copies of the SAME
 * skin resolves to the same key and the same plan, so tearing the renderer down
 * would restart every emitter and drop every particle in flight to redraw an
 * identical ship - a visible flicker caused by an economic change the picture
 * does not have. Swapping definitions changes the key and must rebuild.
 *
 * The modality and the level of detail are compared beside it, because they are
 * the other two things that change what a renderer would BUILD from the same
 * PNG: a plan compiled for `pvp3x3` has fewer layers and lower ceilings than one
 * compiled for `hunt`, and answering "unchanged" for that pair would leave a
 * scene running a budget nobody chose.
 */
export function skinContentChanged(
  previous: ResolvedSkinContent | null,
  next: ResolvedSkinContent | null,
): boolean {
  if (previous === null || next === null) {
    return previous !== next;
  }
  return (
    previous.textureKey !== next.textureKey ||
    previous.plan.modality !== next.plan.modality ||
    previous.plan.lod !== next.plan.lod
  );
}
